import { useMemo } from 'react';

import { endOfTodayIST, startOfTodayIST, todayISTKey } from '../lib/dateIST.js';

import { useWeightLogsQuery } from './useWeightLogs.js';

const DISMISS_KEY_PREFIX = 'onflow.weightPromptDismissed.';

/**
 * Gate logic for the daily weight-logging prompt modal.
 *
 * Combines server-truth ("did the user log a weight today?" via the existing
 * /logs/weight query, filtered to the IST calendar day) with client state
 * ("did the user dismiss the prompt today?" via a per-day localStorage key).
 * The hook returns `shouldShow=true` only when (a) the query has succeeded,
 * (b) no log exists for today, and (c) no dismiss key exists for today —
 * fail-closed on any indeterminate state so the user is never nagged on bad
 * data.
 *
 * The localStorage wrapper swallows throws so Safari private mode and
 * blocked-storage environments degrade gracefully: dismiss becomes a no-op
 * (prompt re-fires on next mount) instead of crashing the gate.
 */

interface SafeStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export const safeLocalStorage: SafeStorage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private mode / quota exceeded / storage blocked — silent degrade.
    }
  },
};

export interface WeightPromptGate {
  shouldShow: boolean;
  isLoading: boolean;
  dismiss: () => void;
}

export function useWeightPromptGate(): WeightPromptGate {
  const todayKey = todayISTKey();
  const dismissKey = `${DISMISS_KEY_PREFIX}${todayKey}`;

  const queryArgs = useMemo(
    () => ({
      page: 1,
      pageSize: 1,
      from: startOfTodayIST(),
      to: endOfTodayIST(),
    }),
    // todayKey rotates the cache once per IST calendar day; safe to depend on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [todayKey],
  );

  const todayQuery = useWeightLogsQuery(queryArgs);

  const dismissed = safeLocalStorage.get(dismissKey) === '1';
  const loggedToday = (todayQuery.data?.items.length ?? 0) > 0;
  const shouldShow = !dismissed && todayQuery.isSuccess && !loggedToday;

  return {
    shouldShow,
    isLoading: todayQuery.isLoading,
    dismiss: () => {
      safeLocalStorage.set(dismissKey, '1');
    },
  };
}

/**
 * Removes stale dismiss keys for prior IST days. Idempotent; safe to call on
 * every authenticated layout mount. Keeps localStorage from accumulating
 * unbounded dismiss markers across long-lived sessions.
 */
export function cleanupStaleDismissKeys(now: Date = new Date()): void {
  const todayKey = todayISTKey(now);
  let storage: Storage;
  try {
    storage = localStorage;
  } catch {
    return;
  }
  const toRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && key.startsWith(DISMISS_KEY_PREFIX)) {
      const dayKey = key.slice(DISMISS_KEY_PREFIX.length);
      if (dayKey !== todayKey) toRemove.push(key);
    }
  }
  for (const key of toRemove) {
    try {
      storage.removeItem(key);
    } catch {
      // ignore
    }
  }
}
